import { bomClient } from '../client/bomClient';
import { trpClient } from '../client/trpClient';
import { findComponentIndex } from '../pages/wizard/validation/componentMatch';
import type { ComponentWithHistory } from '../pages/wizard/validation/types';
import { isSupplierShortlisted, suppliersOf } from '../components/discovery/discoveryModel';
import type { RobbieAppliedEvent } from './robbieAppliedBus';
import type {
  ContextAnswersPayload,
  CriteriaPayload,
  RobbieProposal,
  ShortlistPayload,
  ValidationPayload,
} from '../types/robbieProposal';

/**
 * Applying a Robbie card: save through the endpoint the step's own controls
 * use, then say what changed so the screen updates (`RobbieAppliedEvent`).
 * Cards that only fill a working copy — the outreach composer, the award
 * builder — save nothing: the buyer's own Send and Confirm award stay the
 * only way those leave the browser.
 *
 * Plain async functions with their effects passed in, so the rules are tested
 * without React.
 */
export interface ApplyEffects {
  publish: (event: RobbieAppliedEvent) => void;
  /** Re-reads the RFQ in place; `keepCriteria` leaves the step-2 draft alone. */
  refreshRfq: (keepCriteria: boolean) => Promise<unknown>;
  /** Writes saved criteria into the step-2 form's draft. */
  applyCriteriaToDraft: (changes: CriteriaPayload['changes'], saved: Record<string, unknown>) => void;
}

const applyCriteria = async (payload: CriteriaPayload, fx: ApplyEffects) => {
  const saved = await trpClient.patchRequirements(payload.rfqId, payload.changes);
  // Only the named fields move in the form; anything else the buyer has
  // changed but not saved stays as they left it.
  await fx.refreshRfq(true);
  fx.applyCriteriaToDraft(payload.changes, saved);
};

const applyContextAnswers = async (payload: ContextAnswersPayload, fx: ApplyEffects) => {
  await bomClient.saveContextAnswers(payload.bomId, payload.answers);
  fx.publish({ kind: 'context_answers', bomId: payload.bomId, answers: payload.answers });
};

const applyValidation = async (payload: ValidationPayload, fx: ApplyEffects) => {
  const needsComponents = payload.actions.some((a) => a.fix?.type === 'swap');
  const components = needsComponents ? (await bomClient.getBOMStatus(payload.bomId)).components || [] : [];
  // Fixes first, one at a time — each is a BOM write the next may depend on —
  // then every verdict in one save.
  for (const action of payload.actions) {
    const fix = action.fix;
    if (!fix) continue;
    if (fix.type === 'set_value') await bomClient.saveContextAnswers(payload.bomId, { [fix.field]: fix.value });
    if (fix.type === 'add_components') {
      for (const component of fix.components) await bomClient.addComponent(payload.bomId, component);
    }
    if (fix.type === 'swap') {
      const index = findComponentIndex(components as ComponentWithHistory[], fix.originalComponentName);
      if (index === -1) throw new Error(`I couldn't find "${fix.originalComponentName}" on the BOM any more`);
      await bomClient.replaceComponent(payload.bomId, index, fix.alternative);
    }
  }
  await bomClient.saveAnalysisReviewState(
    payload.bomId,
    Object.fromEntries(payload.actions.map((a) => [a.key, a.verdict]))
  );
  await fx.refreshRfq(true);
  fx.publish({ kind: 'validation_actions', bomId: payload.bomId });
};

/** The new list per component: what is ticked on the server now, plus and minus the card. */
export const shortlistLists = (components: readonly unknown[], payload: ShortlistPayload) =>
  payload.components.map((change) => {
    const component = components.find((row) => {
      const c = row as { componentId?: unknown; id?: unknown; _id?: unknown } | undefined;
      return [c?.componentId, c?.id, c?._id].map((v) => (v === undefined ? v : String(v))).some((v) => v === change.componentId || v === change.componentKey);
    });
    const ticked = suppliersOf(component)
      .filter(isSupplierShortlisted)
      .map((s: { supplierName?: string; name?: string }) => s.supplierName || s.name || '');
    const removed = new Set(change.remove.map((n) => n.toLowerCase()));
    const kept = ticked.filter((n: string) => !removed.has(n.toLowerCase()));
    const added = change.add.filter((n) => !kept.some((k: string) => k.toLowerCase() === n.toLowerCase()));
    return { componentId: change.componentId, componentKey: change.componentKey, suppliers: [...kept, ...added] };
  });

const applyShortlist = async (payload: ShortlistPayload, fx: ApplyEffects) => {
  const { components = [] } = await bomClient.getBOMStatus(payload.bomId);
  const lists = shortlistLists(components, payload);
  await bomClient.shortlistSuppliersBatch(payload.bomId, {
    components: lists.map((l) => ({ componentId: l.componentId, suppliers: l.suppliers })),
  });
  await fx.refreshRfq(true);
  fx.publish({ kind: 'shortlist', bomId: payload.bomId, lists });
};

export const applyRobbieProposal = async (proposal: RobbieProposal, fx: ApplyEffects): Promise<void> => {
  switch (proposal.kind) {
    case 'sourcing_criteria':
      return applyCriteria(proposal.payload, fx);
    case 'context_answers':
      return applyContextAnswers(proposal.payload, fx);
    case 'validation_actions':
      return applyValidation(proposal.payload, fx);
    case 'shortlist':
      return applyShortlist(proposal.payload, fx);
    case 'outreach_draft':
      fx.publish({ kind: 'outreach_draft', ...proposal.payload });
      return undefined;
    case 'award_split':
      fx.publish({ kind: 'award_split', ...proposal.payload });
      return undefined;
    default:
      throw new Error("I don't know how to apply that card yet");
  }
};
